import {
  deregisterFromEvent,
  millisToSeconds,
  registerForEvent,
  toDecimalPoints
} from "./utils.ts";
import { makeSystemEvent } from "./models.ts";
import { SYSTEM_EVENT, VISIBLE } from "./constants.ts";
import Controller from "./Controller.ts";
import Logger from "./Logger.ts";

// --- Types ---

interface EventHandlerRecord {
  type: string;
  handler: EventListenerOrEventListenerObject;
}

interface ScrollData {
  [key: string]: unknown;
}

const VISIBILITY_CHANGE_EVENT = "visibilitychange";
const SCROLL_EVENT_TYPES = ["scroll", "touchmove"];

/**
 * Monitors System events and User events
 */
export default class SystemEventMonitor {
  private _isFirst: boolean = true;
  private _lastBackgroundTime: number = -1;
  private _handlers: EventHandlerRecord[] = [];
  private _scrollEvents: unknown[] = []; // Type adjusted based on event structure
  private _scrollData: ScrollData = {};

  constructor(isFirst: boolean = true) {
    this._isFirst = isFirst;
    this.listenSystemEvents();
  }

  /**
   * Registers listeners for system-level events like visibility change
   */
  public listenSystemEvents = (): void => {
    Logger.debug("Registering for System Event ", VISIBILITY_CHANGE_EVENT);

    const handler: EventListener = () => {
      const controller = Controller.getInstance();

      const time: number = controller.getSDKRunningTimeInSec();
      const isForeground: boolean = window.document.visibilityState === VISIBLE;

      // generate system event object
      const systemEvent = makeSystemEvent(
        window.document.visibilityState,
        time
      );

      if (isForeground) {
        controller.setBlockChunkReportingFlag(false);

        if (this._lastBackgroundTime > -1) {
          // Calculate how long the app was in background
          systemEvent["background_time"] = toDecimalPoints(
            millisToSeconds(Date.now() - this._lastBackgroundTime)
          );

          controller.sendOldChunk(() => {
            controller.dispatchEvent(SYSTEM_EVENT, systemEvent);
          });
        }
      } else {
        controller.setBlockChunkReportingFlag(true);
        // Report the chunk immediately when app goes to background
        controller.reportChunkOnVisibilityChange();
        // controller.flushAndReportVideoChunk();
        this._lastBackgroundTime = Date.now();
        controller.dispatchEvent(SYSTEM_EVENT, systemEvent);
      }

      // Handle Navigation/Page View Logic
      if (isForeground) {
        const userAttrs = controller.getUserAttributes();
        const browser = (userAttrs.klaritics_browser as string) || "";

        if (
          !this._isFirst ||
          (browser.indexOf("Mobile Safari") !== -1 &&
            browser.indexOf("Safari") !== -1)
        ) {
          // controller.logPageView(window.location.pathname);
        } else {
          this._isFirst = false;
        }
      } else {
        controller.clearNavigation();
      }
    };

    registerForEvent(document, VISIBILITY_CHANGE_EVENT, handler);
    this._handlers.push(this._newHandler(VISIBILITY_CHANGE_EVENT, handler));
  };

  /**
   * Unregisters for all auto track events to prevent memory leaks
   */
  public unregisterEvents = (): void => {
    this._handlers.forEach(({ type, handler }) => {
      Logger.debug("Unregistering from event type ", type);

      // Select the correct target based on event type
      const target = SCROLL_EVENT_TYPES.includes(type)
        ? window.document
        : document;
      deregisterFromEvent(target, type, handler);
    });

    this._handlers = [];
    this._scrollEvents = [];
    this._scrollData = {};
  };

  /**
   * Gives the final scroll events, calculated from listened events
   */
  public getScrollEvents = (): string[] => {
    return [];
  };

  /**
   * Helper to structure handler records
   */
  private _newHandler = (
    type: string,
    handler: EventListener
  ): EventHandlerRecord => {
    return { type, handler };
  };
}
